// Target Reviews focus slider (2026-09-06, fifth build; 2026-10-03, silent loops).
// One scroll-snap rail. The card nearest the centre is the focus: bright, its
// 10 s silent loop playing, the rest dimmed and showing their poster. A timer
// turns the rail every `turn_seconds`, paused while the pointer or a finger is
// on the rail, while the tab is hidden, and while the rail is off screen.
//
// 2026-10-03: the YouTube player, its postMessage API, the pre-roll/warm
// window and the sound button are gone. Every card is a <video muted loop
// playsinline> with no src until it is needed (the focus card and the one after
// it), plus a poster <img> that is always in the page underneath. A card with no
// loop (data-has-loop="false") is just its poster.
//
// Click model:
//   a dimmed card                  -> becomes the focus (scrolls to it)
//   the focus card's video / title -> the product page (they are real links)
//   Add to cart                    -> the card's own form (cart drawer)
//   the play button                -> plays the loop (only shown when autoplay
//                                     is off or was refused)
//
// Autoplay refused (Safari Low Power Mode, or ?tu-lpm=1 to preview it): a card
// with a loop shows that MP4 inside an <img> (Safari animates it even in Low
// Power Mode); where the browser cannot (the <img> errors), the poster stays
// with a play button. prefers-reduced-motion: no autoplay, no turn, poster +
// play button.
//
// 2026-10-04, loading and turning (user: image loading, "let the video finish and
// then flip", and "?tu-lpm=1 ... the flipping is what's stopped"):
//   * Posters: every <img data-tu-poster> ships with data-src/data-srcset only.
//     The focus card and its neighbours (clones included) get their src at once,
//     the rest as the rail is scrolled or turned to within one card of them
//     (wakeNear). 49 posters + 49 blur copies loaded at page load before.
//   * Blur fill (--tu-trv-bg) is set from the poster's own currentSrc once it has
//     loaded: the same URL, already cached, not a second 400px download.
//   * Clips: no MP4 request until the section is within a screen of the viewport
//     (`near`), and then only the focus card's. The next card's clip is fetched
//     only while the focus clip is in its last 3 s.
//   * The turn is driven by the clip, not by turn_seconds: tick() flips when the
//     focus clip has played one full pass (a <video>: currentTime wraps / reaches
//     the end; a Low Power <img>: the card's data-tu-dur counted from its load; the
//     ?tu-lpm=1 stand-in <video>: its own time). The pauses (hover, touch,
//     engaged, hidden, off screen) hold the flip while the clip keeps looping;
//     when they end it flips at the end of the pass. A card with no clip (or a
//     failed one) turns after turn_seconds (a failed Low Power <img>: its dur).
//     Low Power Mode no longer stops the turn.
(function () {
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var simLPM = /[?&]tu-lpm=1(&|$)/.test(location.search);
  var small = window.matchMedia('(max-width: 749px)');
  var SPARE_MS = 12000; // a next card that has not buffered after this long no longer holds the turn

  function api(card) { var s = card && card.closest && card.closest('[data-tu-trv]'); return s && s.tuTrvApi; }
  function vidOf(wrap) { return wrap ? wrap.querySelector('[data-tu-video]') : null; }
  function loopUrl(wrap) { return wrap && (small.matches && wrap.dataset.loopPhone ? wrap.dataset.loopPhone : wrap.dataset.loop); }
  function isManual(card) { return card.classList.contains('is-manual'); }

  // --- The video element -----------------------------------------------------
  function ensureSrc(wrap) {
    var v = vidOf(wrap), url = loopUrl(wrap);
    if (!v || !url) return null;
    if (v.dataset.cur !== url) {
      delete v.dataset.err;
      v.dataset.cur = url; v.preload = 'auto'; v.src = url; v.load();
    }
    return v;
  }
  function dropSrc(card, wrap) {
    var v = vidOf(wrap); if (!v) return;
    resetCard(card, wrap);
    if (v.dataset.cur) { delete v.dataset.cur; delete v.dataset.err; v.removeAttribute('src'); v.load(); }
  }
  // Back to the poster: paused, rewound, no frame showing.
  function resetCard(card, wrap) {
    var v = vidOf(wrap);
    if (v) { clearTimeout(v._tuManualCheck); v.pause(); try { v.currentTime = 0; } catch (e) {} }
    card.classList.remove('has-frame', 'is-playing', 'is-manual');
    hideLoop(card, wrap);
  }
  // Off screen / tab hidden: stop, but keep the frame on show so coming back is not a poster flash.
  function holdCard(card, wrap) {
    var v = vidOf(wrap); if (!v) return;
    clearTimeout(v._tuManualCheck); v.pause();
  }
  function bindVideo(card, wrap) {
    var v = vidOf(wrap); if (!v) return;
    v.addEventListener('playing', function () {
      // Only the focus card plays. A play() that was still loading when the rail
      // moved on (measured in WebKit) can start after resetCard() paused it: stop it here.
      if (!card.classList.contains('is-focus')) { v.pause(); try { v.currentTime = 0; } catch (e) {} return; }
      card.classList.add('has-frame', 'is-playing');
      var wasManual = isManual(card);
      card.classList.remove('is-manual'); hideLoop(card, wrap);
      var a = api(card);
      // a card started by hand: the visitor is watching, so the rail stops turning
      if (wasManual && a && a.engage) a.engage(card);
      if (a && a.playing) a.playing(card);
    });
    v.addEventListener('pause', function () { card.classList.remove('is-playing'); });
    v.addEventListener('error', function () {
      if (!v.getAttribute('src')) return; // dropSrc clears the src, which is not a failure
      v.dataset.err = '1'; card.classList.remove('has-frame', 'is-playing');
    });
    v.removeAttribute('autoplay'); v.pause();
  }

  // Start a card's loop. userTap: from the play button (a real gesture).
  function cardPlay(card, wrap, userTap) {
    var a = api(card);
    // 2026-10-04: the preview refuses before any <video> src is set (the hidden <video> used to download the clip as well as the stand-in)
    if (simLPM && !userTap && wrap && vidOf(wrap)) { if (a && a.blocked) a.blocked(); if (card.classList.contains('is-focus')) markManual(card, wrap); return; }
    var v = ensureSrc(wrap); if (!v) return;
    var p = v.play();
    if (p && p.then) p.then(function () { if (!card.classList.contains('is-focus')) { v.pause(); } }).catch(function (err) {
      if (!err || err.name !== 'NotAllowedError') return; // an aborted load (turned away from) is not a refusal
      if (a && a.blocked) a.blocked();
      if (!userTap && card.classList.contains('is-focus')) markManual(card, wrap);
    });
    // play() can be accepted yet never start (Low Power Mode); .paused stays true.
    if (!userTap) {
      clearTimeout(v._tuManualCheck);
      v._tuManualCheck = setTimeout(function () {
        if (v.paused && v.dataset.err !== '1' && card.classList.contains('is-focus') && !(a && a.isHidden())) {
          markManual(card, wrap);
          if (a && a.blocked) a.blocked();
        }
      }, 2500);
    }
  }

  // --- Manual / Low Power Mode -----------------------------------------------
  function markManual(card, wrap) {
    if (!card || !wrap || isManual(card) || card.dataset.hasLoop !== 'true') return;
    card.classList.add('is-manual');
    if (!reduced) showLoop(card, wrap);
  }
  // Safari plays a silent MP4 given to an <img>, even when it refuses every
  // <video> autoplay. Chrome and Firefox cannot, so ?tu-lpm=1 uses a muted
  // <video> there (and only there); a real visitor without support gets the
  // poster and the play button instead.
  function showLoop(card, wrap) {
    var url = loopUrl(wrap);
    if (!url || wrap.querySelector('.tu-trv__loop')) return;
    var img = document.createElement(simLPM ? 'video' : 'img');
    img.className = 'tu-trv__loop'; img.setAttribute('aria-hidden', 'true');
    if (simLPM) { img.muted = true; img.loop = true; img.playsInline = true; img.setAttribute('muted', ''); img.setAttribute('playsinline', ''); }
    else img.alt = '';
    // _tuLoopAt: when the loop started, so tick() can time the <img> (it gives no clip time)
    img.addEventListener(simLPM ? 'loadeddata' : 'load', function () { if (img.isConnected) { card._tuLoopAt = performance.now(); card.classList.add('has-loop'); } });
    img.addEventListener('error', function () { img.remove(); card._tuLoopAt = 0; card.classList.remove('has-loop'); });
    img.src = url;
    wrap.appendChild(img);
    if (simLPM) { var pp = img.play(); if (pp && pp.catch) pp.catch(function () {}); }
  }
  function hideLoop(card, wrap) {
    var l = wrap && wrap.querySelector('.tu-trv__loop'); if (l) l.remove();
    if (card) { card._tuLoopAt = 0; card.classList.remove('has-loop'); }
  }

  function bind(section) {
    if (section.tuTrvBound) return; section.tuTrvBound = true;
    var rail = section.querySelector('[data-tu-trv-rail]'); if (!rail) return;

    var inView = false, heroDone = false, near = false, blocked = false;
    var cur = -1, t = 0, last = 0, hover = false, touching = false, engaged = false, raf = null, signalled = false, focusAt = 0;
    var tickTimer = null, lastTickAt = 0;
    // turn state (2026-10-04): armed = the focus clip is mid-pass, so reaching its end is a real pass end
    var building = false, quietUntil = 0, quietTimer = null;
    var armed = false, prevCt = -1, lastKey = null, pendingFlip = false, pendingAt = 0, holdUntil = 0, lastF = 0;
    var bar = section.querySelector('[data-tu-trv-bar]');
    // 2026-10-04: the fill is a transform (scaleX), not a width: a width transition re-laid out and repainted the
    // page on every frame while the rail was on screen (~1.4 s of 3.7 s main-thread time per 10 s, desktop).
    var dots = section.querySelector('[data-tu-trv-dots]');
    var secs = parseInt(section.dataset.tuTurn || '5', 10) || 0;
    var focusAuto = section.dataset.tuFocusAutoplay === 'on' && !reduced;

    // Once this page has seen an autoplay refused, cards show their loop as an <img> and no
    // next-clip is fetched ahead. (Until 2026-10-04 this also stopped the turn and hid the
    // bar; it no longer does, tick() times the <img> from data-tu-dur.)
    function setBlocked() {
      if (blocked) return;
      blocked = true;
      section.classList.add('is-autoplay-blocked');
      if (bar && bar.parentElement) bar.parentElement.classList.remove('is-waiting');
    }
    function resumeFocus() {
      if (inView && heroDone && focusAuto && !engaged && !document.hidden && cards && cards[cur] && wraps && wraps[cur] && cards[cur].dataset.hasLoop === 'true') {
        if (isManual(cards[cur])) return;
        cardPlay(cards[cur], wraps[cur], false);
      }
    }
    // The focus card's clip is requested once the section is near the viewport (and the hero is done), not before.
    function fetchFocus() {
      if (blocked || reduced || !focusAuto || !heroDone || !near || cur < 0 || !n) return;
      var c = cards[cur], w = wraps[cur];
      if (c && w && c.dataset.hasLoop === 'true' && !isManual(c)) ensureSrc(w);
    }
    // The card after the focus is fetched ahead (preload auto) so the turn lands on a playing frame --
    // only while the focus clip is in its last 3 s and the section is on screen.
    function warmNext() {
      if (blocked || reduced || !heroDone || !inView || cur < 0 || !n) return;
      var c = cards[cur], v = vidOf(wraps[cur]);
      if (!c || !v || !c.classList.contains('is-playing') || !(v.duration > 0) || v.duration - v.currentTime > 3) return;
      var w = wraps[(cur + 1) % n]; if (w && w.dataset.loop) ensureSrc(w);
    }
    function isLoaded(wrap) {
      var v = vidOf(wrap);
      if (!wrap || !v || !wrap.dataset.loop) return true;
      if (v.dataset.err === '1') return true;
      return v.readyState >= 3 && !!v.dataset.cur;
    }
    // 2026-10-06 (hero speed, load order): "hero done" now also means the two small hero tiles have arrived (tu-loader.js fires
    // tu:tiles-loaded and sets data-tu-tiles-loaded on <html>; 3.5 s fallback), so this rail's posters and clips queue behind
    // the main hero AND the tiles, in page order. Posters are not woken (wakePoster) until then.
    var onHeroDone = function () { if (heroDone) return; heroDone = true; if (typeof slots !== "undefined" && slots && slots.length) wakeNear(); resumeFocus(); fetchFocus(); };
    var whenTiles = function (cb) {
      if (document.documentElement.hasAttribute('data-tu-tiles-loaded') || !document.querySelector('.tu-hT__tile--small img[data-tu-src], .tu-hT__tile--small img[src^="data:"]')) return cb();
      var once = function () { document.removeEventListener('tu:tiles-loaded', once); cb(); };
      document.addEventListener('tu:tiles-loaded', once); setTimeout(once, 3500);
    };
    var heroImg = document.querySelector('img.tu-hM__bg');
    if (heroImg) {
      if (heroImg.complete) whenTiles(onHeroDone); else heroImg.addEventListener('load', function () { whenTiles(onHeroDone); }, { once: true });
    } else {
      var heroTile = document.querySelector('[data-tu-hero]');
      var heroMatch = heroTile && /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(heroTile).backgroundImage);
      if (!heroMatch) heroDone = true; // no hero tile on this template -- nothing to wait for
      else if (document.documentElement.hasAttribute('data-tu-hero-loaded')) whenTiles(onHeroDone);
      else {
        document.addEventListener('tu:hero-loaded', function () { whenTiles(onHeroDone); }, { once: true });
        setTimeout(function () { if (heroDone) return; var p = new Image(); p.addEventListener('load', function () { whenTiles(onHeroDone); }, { once: true }); p.addEventListener('error', function () { whenTiles(onHeroDone); }, { once: true }); p.src = heroMatch[1]; }, 6000);
      }
    }
    // Off-screen (< 25% visible): the focus loop stops and the turn timer
    // freezes. A second observer (on the rail) gates the clip fetch.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var was = inView;
        inView = entries.some(function (e) { return e.isIntersecting; });
        if (was && !inView && cards && cards[cur] && wraps && wraps[cur]) { holdCard(cards[cur], wraps[cur]); engaged = false; }
        if (!was && inView) resumeFocus();
        if (inView && !document.hidden) startTick(); else stopTick();
      }, { threshold: 0.25 }).observe(section);
      new IntersectionObserver(function (entries) {
        var wasNear = near;
        near = entries.some(function (e) { return e.isIntersecting; });
        if (!wasNear && near) { fetchFocus(); wakeNear(); }
      }, { rootMargin: '0px' }).observe(rail);
      // 2026-10-04: `near` is "the rail itself is on screen", not a screen of margin around the section. Measured on
      // the mirror: the section's heading peeks into the first screen (top at 746-765 px of an 844-900 px viewport)
      // but the rail starts ~200 px lower, so a one-screen margin fetched a clip nobody could see yet. The clip
      // plays at 25% of the section visible, which is about when the rail comes on screen.
    } else { inView = true; near = true; }

    section.tuTrvApi = {
      inView: function () { return inView; },
      isHidden: function () { return document.hidden || !inView; },
      engage: function (card) { if (cards.indexOf(card) === cur) engaged = true; },
      blocked: setBlocked,
      playing: function (card) { if (cards.indexOf(card) === cur) fireReady(); }
    };

    // allItems is the permanent set of real <li>s -- never cloned, never removed.
    // A category filter only decides which of THESE are the active set.
    var allItems = [].slice.call(rail.querySelectorAll('.tu-scroller__item'));
    if (!allItems.length) return;
    var track = allItems[0].parentElement;
    var items = [], cards = [], wraps = [], n = 0;
    var preClones = [], postClones = [], slots = [], CL = 0;

    function matches(el, active) {
      if (!active.length) return true;
      var card = el.querySelector('[data-tu-card]');
      var cats = ((card && card.dataset.cats) || '').split(/\s+/).filter(Boolean);
      return cats.some(function (c) { return active.indexOf(c) !== -1; });
    }

    // Loop: two display-only copies of the last cards before the first, two of
    // the first after the last, so a card always shows in both peeks. A copy is
    // a poster and a bar: its form is unwrapped (the button stays, for the look)
    // and the whole thing is inert; a tap on it turns the rail to the real card.
    function makeClone(item, realIndex) {
      var c = item.cloneNode(true);
      c.querySelectorAll('video, .tu-trv__loop, .tu-trv__play').forEach(function (el) { el.remove(); });
      c.querySelectorAll('form').forEach(function (f) { while (f.firstChild) f.parentNode.insertBefore(f.firstChild, f); f.remove(); });
      c.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
      c.querySelectorAll('input[type="hidden"]').forEach(function (el) { el.remove(); });
      c.querySelectorAll('[data-tu-card]').forEach(function (el) { el.removeAttribute('data-tu-card'); el.setAttribute('data-tu-clone', realIndex); el.classList.remove('is-focus', 'is-playing', 'has-frame', 'is-manual', 'has-loop'); });
      c.setAttribute('aria-hidden', 'true'); c.setAttribute('inert', ''); c.dataset.tuClone = realIndex;
      c.addEventListener('click', function (e) {
        e.preventDefault(); engaged = false;
        var r = parseInt(c.dataset.tuClone, 10);
        goTo(preClones.indexOf(c) >= 0 ? r - n : r + n);
      });
      return c;
    }
    function realOf(sIdx) { return ((sIdx - CL) % n + n) % n; }

    // --- Posters (2026-10-04) --------------------------------------------------
    // The blurred fill behind a card is the poster itself: same URL (currentSrc), already cached.
    function fillBg(img) {
      var w = img.parentNode; if (!w || !w.style || w.style.getPropertyValue('--tu-trv-bg')) return;
      var set = function () { if (img.currentSrc) w.style.setProperty('--tu-trv-bg', 'url("' + img.currentSrc + '")'); };
      if (img.complete && img.currentSrc) set(); else img.addEventListener('load', set, { once: true });
    }
    function wakePoster(li) {
      var img = li && li.querySelector('img[data-tu-poster]'); if (!img) return;
      if (!heroDone) return;   // 2026-10-06: queued behind the hero and the small tiles (onHeroDone wakes them)
      if (img.dataset.src) {
        // lazy for the first three at load (a section far below the fold fetches nothing); eager once it is near
        if (near || inView) img.loading = 'eager';
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src; delete img.dataset.srcset;
      }
      if (img.getAttribute('src')) fillBg(img);
    }
    // The three slots around one slot (the focus and its neighbours, clones included).
    // A slot is woken on its own, not together with its twin (a real card and its clone share one poster URL): the twin
    // is woken in onSettle() just before the rail jumps onto it, by when the URL is cached. Waking both at once made
    // Chromium request one URL twice (measured, phone).
    function wakeAround(sIdx) { for (var d = -1; d <= 1; d++) if (slots[sIdx + d]) wakePoster(slots[sIdx + d]); }
    // Posters of the cards within one card of the rail's centre (the focus, its neighbours, and whatever a swipe is passing).
    function wakeNear() {
      if (!slots.length || (performance.now() < quietUntil && cur >= 0)) { if (cur >= 0 && slots.length) wakeAround(CL + cur); return; }
      var c = rail.scrollLeft + rail.clientWidth / 2;
      var pitch = slots.length > 1 ? slots[1].offsetLeft - slots[0].offsetLeft : slots[0].offsetWidth;
      if (!(pitch > 0)) pitch = slots[0].offsetWidth || 1;
      slots.forEach(function (el, sIdx) {
        if (Math.abs(el.offsetLeft + el.offsetWidth / 2 - c) <= pitch * 1.6) wakePoster(el);
      });
    }

    function buildClones() {
      preClones.forEach(function (el) { el.remove(); });
      postClones.forEach(function (el) { el.remove(); });
      preClones = []; postClones = [];
      CL = Math.min(2, n);
      for (var k = n - CL; k < n; k++) preClones.push(makeClone(items[k], k));
      for (var k2 = 0; k2 < CL; k2++) postClones.push(makeClone(items[k2], k2));
      preClones.forEach(function (el) { track.insertBefore(el, items[0]); });
      postClones.forEach(function (el) { track.appendChild(el); });
      slots = preClones.concat(items, postClones);
    }

    function buildDots() {
      if (!dots) return;
      dots.innerHTML = '';
      cards.forEach(function (c, i) {
        var b = document.createElement('button'); b.type = 'button'; b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', 'Review ' + (i + 1) + ' of ' + n);
        b.addEventListener('click', function () { engaged = false; goTo(i); });
        dots.appendChild(b);
      });
    }

    function build(activeItems) {
      items = activeItems;
      cards = items.map(function (i) { return i.querySelector('[data-tu-card]'); });
      wraps = cards.map(function (c) { return c ? c.querySelector('[data-tu-video-wrap]') : null; });
      n = items.length;
      allItems.forEach(function (el) { el.hidden = items.indexOf(el) === -1; });
      allCards.forEach(function (c) { if (c && cards.indexOf(c) === -1) c.classList.remove('is-focus'); });
      section.classList.toggle('is-empty', n === 0);
      if (!n) {
        preClones.forEach(function (el) { el.remove(); }); postClones.forEach(function (el) { el.remove(); });
        preClones = []; postClones = []; slots = []; CL = 0;
        if (dots) dots.innerHTML = '';
        return;
      }
      buildClones();
      buildDots();
      cur = -1; // force setFocus(0) below to run its DOM work even if cur was already 0
      rail.scrollLeft = targetLeft(0); // set BEFORE setFocus: setFocus reads rail.scrollLeft via nearestSlot()
      // WebKit has not always applied that scrollLeft yet (measured: 0), which woke the two cards left of the real
      // neighbours; at build the focus is known, so wake around it instead of reading the scroll position.
      building = true; setFocus(0); building = false;
      wakeAround(CL); quietUntil = performance.now() + 900; // the rail may still be scrolling to card 0 (WebKit animates it from 0)
    }

    // Reset every card before the active set changes (a filter), dropping every loop.
    function killAll() {
      allItems.forEach(function (el, k) { var c = allCards[k], w = allWraps[k]; if (c && w) dropSrc(c, w); });
    }

    function applyChips() {
      var active = chips.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })
                        .map(function (b) { return b.dataset.cat; });
      killAll();
      build(allItems.filter(function (el) { return matches(el, active); }));
      engaged = false;
    }

    var chips = [].slice.call(section.querySelectorAll('[data-tu-trv-chip]'));
    chips.forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.setAttribute('aria-pressed', btn.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        applyChips();
      });
    });

    function leftOf(el) { return el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2; }
    function targetLeft(i) { return leftOf(items[i]); }
    // goTo takes a real index; -1 and n are allowed once, to ride a clone and then jump.
    function goTo(i) {
      if (!n) return;
      t = 0;
      var el = i < 0 ? preClones[preClones.length + i] : (i >= n ? postClones[i - n] : items[i]);
      if (!el) { el = items[((i % n) + n) % n]; }
      rail.scrollTo({ left: leftOf(el), behavior: reduced ? 'auto' : 'smooth' });
    }
    function nearestSlot() {
      if (!slots.length) return 0;
      var c = rail.scrollLeft + rail.clientWidth / 2, best = 0, bd = Infinity;
      slots.forEach(function (it, sIdx) { var d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - c); if (d < bd) { bd = d; best = sIdx; } });
      return best;
    }
    function nearest() { return n ? realOf(nearestSlot()) : 0; }
    var settleTimer = null;
    function onSettle() {
      var sIdx = nearestSlot(); var el = slots[sIdx];
      if (el.dataset.tuClone !== undefined) {
        var prevB = rail.style.scrollBehavior; rail.style.scrollBehavior = 'auto';
        wakePoster(items[realOf(sIdx)]); // its clone's poster is already in the cache: no flash, no second download
        rail.scrollLeft = leftOf(items[realOf(sIdx)]);
        requestAnimationFrame(function () { rail.style.scrollBehavior = prevB; });
      }
    }
    function fireReady() {
      if (signalled === 'done') return; signalled = 'done';
      section.dataset.tuReviewsReady = '1'; section.dispatchEvent(new CustomEvent('tu:reviews-ready', { bubbles: true }));
    }
    function setFocus(i) {
      if (!n) return;
      if (i === cur) return;
      cur = i; t = 0; focusAt = Date.now();
      armed = false; prevCt = -1; pendingFlip = false;
      if (bar) { bar.style.transition = 'none'; bar.style.transform = 'scaleX(0)'; void bar.offsetWidth; bar.style.transition = ''; }
      engaged = false;
      var nxt = (i + 1) % n;
      var keep = {}; [-1, 0, 1].forEach(function (d) { keep[(i + d + n) % n] = true; });
      var sNow = nearestSlot();
      slots.forEach(function (el, sIdx) { var cl = el.querySelector('[data-tu-clone]'); if (cl) cl.classList.toggle('is-focus', sIdx === sNow && realOf(sIdx) === i); });
      cards.forEach(function (c, j) {
        if (!c) return;
        c.classList.toggle('is-focus', j === i);
        if (!wraps[j]) return;
        if (j === i) return;
        if (!keep[j]) dropSrc(c, wraps[j]); else resetCard(c, wraps[j]);
      });
      if (dots) [].forEach.call(dots.children, function (d, j) { d.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
      if (!building) wakeNear();
      fetchFocus();
      var card = cards[i], wrap = wraps[i];
      if (card && wrap && card.dataset.hasLoop === 'true') {
        if (focusAuto && inView && heroDone && !document.hidden) cardPlay(card, wrap, false);
        else if (!focusAuto) markManual(card, wrap);
        // else: the hero or the viewport is not ready; resumeFocus() runs when each flips
      }
      // A first-screen signal for tu-loader.js: the first card is playing, or 3 s passed.
      if (!signalled) {
        signalled = true;
        if (!card || card.dataset.hasLoop !== 'true') fireReady();
        setTimeout(fireReady, 3000);
      }
    }
    rail.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(function () { raf = null;
        // right after build WebKit scrolls the rail to card 0 from 0, passing the left clones: do not let that pass become the focus (it fetched card 40's poster and clip)
        var q = quietUntil - performance.now(); if (q > 0) { clearTimeout(quietTimer); quietTimer = setTimeout(function () { rail.dispatchEvent(new Event('scroll')); }, q + 50); return; }
        var i = nearest(); setFocus(i); wakeNear(); var sNow = nearestSlot(); slots.forEach(function (el, sIdx) { var cl = el.querySelector('[data-tu-clone]'); if (cl) cl.classList.toggle('is-focus', sIdx === sNow && realOf(sIdx) === i); }); });
      if (!('onscrollend' in window)) { clearTimeout(settleTimer); settleTimer = setTimeout(onSettle, 200); }
    }, { passive: true });
    if ('onscrollend' in window) rail.addEventListener('scrollend', onSettle, { passive: true });

    var prev = section.querySelector('[data-tu-scroll-prev]'), next = section.querySelector('[data-tu-scroll-next]');
    function step(d) { engaged = false; var i = cur + d; if (i < -1) i = -1; if (i > n) i = n; goTo(i); }
    if (prev) { prev.disabled = false; prev.addEventListener('click', function () { step(-1); }); }
    if (next) next.addEventListener('click', function () { step(1); });
    rail.addEventListener('keydown', function (e) { if (e.key === 'ArrowRight') { e.preventDefault(); step(1); } if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); } });

    section.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hover = true; });
    section.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hover = false; });
    rail.addEventListener('touchstart', function () { touching = true; }, { passive: true });
    rail.addEventListener('touchend', function () { setTimeout(function () { touching = false; }, 2500); }, { passive: true });

    // Bound ONCE against the permanent real cards; `i` is looked up at click time.
    var allCards = allItems.map(function (i) { return i.querySelector('[data-tu-card]'); });
    var allWraps = allCards.map(function (c) { return c ? c.querySelector('[data-tu-video-wrap]') : null; });
    allCards.forEach(function (card, physIdx) {
      if (!card) return;
      var wrap = allWraps[physIdx];
      bindVideo(card, wrap);
      card.addEventListener('click', function (e) {
        var i = cards.indexOf(card);
        if (i === -1) return; // hidden by the active filter
        if (i !== cur) { e.preventDefault(); engaged = false; goTo(i); return; }
        if (e.target.closest('a, button, form, input, select, label')) return; // their own job
        var url = card.dataset.cardUrl;
        if (url) { if (/^https?:\/\//.test(url)) window.open(url, '_blank', 'noopener'); else window.location.href = url; }
      });
      var play = card.querySelector('[data-tu-play]');
      if (play) play.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (cards.indexOf(card) !== cur) { engaged = false; goTo(cards.indexOf(card)); return; }
        hideLoop(card, wrap);
        cardPlay(card, wrap, true);
      });
    });

    // What drives the turn for the focus card right now (2026-10-04).
    //   media: a playing clip with a real clock (the <video>, or the ?tu-lpm=1 stand-in <video>)
    //   img:   a Low Power <img> loop; no clock, so its data-tu-dur counted from its load
    //   timer: no clip to wait for (poster only, a failed clip, a failed <img>): a plain wait
    //   wait:  a clip that is still loading or starting; nothing runs until it plays
    function clipDur(card) { var d = parseFloat(card.dataset.tuDur); return d > 0 ? d : 10; }
    function turnSource() {
      var card = cards[cur], wrap = wraps[cur];
      if (!card || !wrap || card.dataset.hasLoop !== 'true') return { kind: 'timer', secs: secs };
      var loopEl = wrap.querySelector('.tu-trv__loop'), v = vidOf(wrap);
      if (isManual(card)) {
        if (loopEl && card.classList.contains('has-loop')) {
          if (loopEl.tagName === 'VIDEO') return loopEl.duration > 0 && !loopEl.paused ? { kind: 'media', el: loopEl } : { kind: 'wait' };
          return card._tuLoopAt ? { kind: 'img', at: card._tuLoopAt, d: clipDur(card) } : { kind: 'wait' };
        }
        return loopEl ? { kind: 'wait' } : { kind: 'timer', secs: clipDur(card) };
      }
      if (v && v.dataset.err === '1') return { kind: 'timer', secs: clipDur(card) };
      if (v && card.classList.contains('is-playing') && v.duration > 0 && !v.paused) return { kind: 'media', el: v };
      return { kind: 'wait' };
    }
    // Low Power Mode: the next card's loop goes into an <img> (or the stand-in <video>) only when it becomes the focus, so
    // its download would eat into that card's turn. Pull it into the HTTP cache in the last 3 s of the current one.
    var warmed = {};
    function prefetchLoop(wrap) {
      var u = loopUrl(wrap); if (!u || warmed[u] || !window.fetch) return;
      warmed[u] = 1; fetch(u, { cache: 'force-cache' }).then(function (r) { return r.arrayBuffer(); }).catch(function () { delete warmed[u]; });
    }
    function flip(why) {
      holdUntil = performance.now() + 1500; pendingFlip = false; armed = false;
      if (bar) bar.style.transform = 'scaleX(1)';
      var log = section.tuTrvLog || (section.tuTrvLog = []);
      log.push({ at: Date.now(), from: cur, why: why }); if (log.length > 60) log.shift();
      goTo(cur + 1);
    }
    function tick() {
      var now = performance.now();
      if (!lastTickAt) lastTickAt = now; var dt = now - lastTickAt; lastTickAt = now;
      if (!n) return;
      warmNext();
      var src = turnSource();
      // The turn waits for the next clip to have buffered (it is fetched in the focus clip's last 3 s), at most 4 s.
      var nextReady = blocked || src.kind !== 'media' || isLoaded(wraps[(cur + 1) % n]);
      var run = secs > 0 && !reduced && !hover && !touching && !engaged && !document.hidden && inView && heroDone;
      var f = 0, ct = 0, d = 0;
      if (src.kind === 'media' || src.kind === 'img') {
        if (src.kind === 'media') { ct = src.el.currentTime; d = src.el.duration; } else { d = src.d; ct = ((now - src.at) / 1000) % d; }
        var key = src.el || src.at;
        if (key !== lastKey) { lastKey = key; armed = false; prevCt = -1; }
        f = Math.min(1, ct / d);
        if (blocked && !reduced && inView && d - ct <= 3) prefetchLoop(wraps[(cur + 1) % n]);
        var wrapped = prevCt >= 0 && ct < prevCt - 0.5;
        if (ct < d - 0.6) armed = true;
        var ended = armed && (wrapped || ct >= d - 0.2);
        prevCt = ct;
        if (ended) { armed = false; if (run && now > holdUntil) { if (nextReady) flip('pass'); else { pendingFlip = true; pendingAt = now; } } }
        else if (pendingFlip) { if (!run) pendingFlip = false; else if (nextReady || now - pendingAt > 4000) flip('pass-late'); }
        if (wrapped && bar) { bar.style.transition = 'none'; bar.style.transform = 'scaleX(0)'; void bar.offsetWidth; bar.style.transition = ''; }
        else if (bar && now > holdUntil) bar.style.transform = 'scaleX(' + f.toFixed(4) + ')';
      } else if (run) {
        t += dt;
        var lim = src.kind === 'wait' ? SPARE_MS : src.secs * 1000;
        f = src.kind === 'wait' ? 0 : Math.min(1, t / lim);
        if (bar && now > holdUntil) bar.style.transform = 'scaleX(' + f.toFixed(4) + ')';
        if (t >= lim) flip(src.kind);
      }
      // Read-only state for audits; no behaviour depends on it.
      section.tuTrvState = { cur: cur, kind: src.kind, run: run, f: Math.round(f * 1000) / 1000, ct: Math.round(ct * 100) / 100, dur: Math.round(d * 100) / 100, nextReady: nextReady, hover: hover, touching: touching, engaged: engaged, blocked: blocked, inView: inView, heroDone: heroDone, hidden: document.hidden, t: Math.round(t) };
      section.classList.toggle('is-paused', !run);
      if (bar && bar.parentElement) bar.parentElement.classList.toggle('is-waiting', src.kind === 'wait');
    }
    function startTick() {
      if (tickTimer) return;
      lastTickAt = 0; armed = false; prevCt = -1; // the focus clip may have been rewound while off screen: not a pass end
      tickTimer = setInterval(tick, 200);
    }
    function stopTick() {
      if (!tickTimer) return;
      clearInterval(tickTimer); tickTimer = null;
      section.tuTrvState = { cur: cur, kind: 'stopped', run: false, nextReady: false, hover: hover, touching: touching, engaged: engaged, blocked: blocked, inView: inView, heroDone: heroDone, hidden: document.hidden, t: Math.round(t) };
      section.classList.add('is-paused');
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stopTick(); if (cards && cards[cur] && wraps[cur]) holdCard(cards[cur], wraps[cur]); }
      else if (inView) { startTick(); resumeFocus(); }
    });
    if (inView && !document.hidden) startTick();
    build(allItems); // initial paint: no chip pressed yet, so this is every card
    // Crossing the phone breakpoint changes which loop a card wants: start over from the focus card.
    var onBreak = function () { if (!n) return; var c = cur; killAll(); cur = -1; rail.scrollTo({ left: targetLeft(c), behavior: 'auto' }); setFocus(c); };
    if (small.addEventListener) small.addEventListener('change', onBreak); else if (small.addListener) small.addListener(onBreak);
    window.addEventListener('resize', function () { if (n) { rail.scrollTo({ left: targetLeft(cur), behavior: 'auto' }); wakeNear(); } });
  }

  function init() { document.querySelectorAll('[data-tu-trv]').forEach(bind); }
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();
